import React, {useEffect, useMemo} from "react"
import {useAccessToken, useIsOrgUser, useIsUnitUser, useUserClaimsData, useUserType} from "../../services/auth"
import {usePaging} from "../../hooks/usePaging"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {Customer, CustomerStatus, CustomerType, exportCustomers, findCustomers} from "../../resources/customer"
import {useQueryState} from "use-location-state"
import SearchBox from "../../components/SearchBox/SearchBox"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {findBanks, Bank} from "../../resources/bank"
import {findClientOrgBankAgreement, ClientOrgBankAgreement} from "../../resources/clientOrgBankAgreement"
import {AsyncResult} from "../../types/asyncResult"
import {ErrorDocument} from "../../resources/common"
import {Filter} from "../Filter/Filter"
import {useToasts} from "react-toast-notifications"
import {downloadFile} from "../../utilities/file"
import {DataTableActionHeader, DataTableCard, TablePending} from "../DataTable/DataTable"
import {isSandboxEnv} from "../../utilities/environment"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {findOrgs} from "../../resources/org"
import {Org} from "../../resources/org"
import {AllowedCustomersColumns} from "./Customer.types"
import CustomersTable from "./CustomersTable"

interface CustomersProps {
	token?: string
	resourcePath?: string
	limit?: number
	includedColumns: Array<AllowedCustomersColumns>
	enableTitle?: boolean
	fullHeight?: boolean
	refreshToken?: number
	onSelectCustomer?: (customer: Customer) => void
}

const customerTypeToFilterOptions: Record<CustomerType, string> = {
	[CustomerType.individual]: "Individual Customer",
	[CustomerType.business]: "Business Customer",
	[CustomerType.businessFBO]: "Business FBO Customer",
	[CustomerType.individualWallet]: "Individual Wallet Customer",
	[CustomerType.businessWallet]: "Business Wallet Customer",
}

const WALLET_ALLOWED_ORG_ID = "4299"
const isOrgUserForWallet = () => {
	const orgId = useUserClaimsData().orgId
	return useIsOrgUser() && isSandboxEnv() && orgId === WALLET_ALLOWED_ORG_ID
}

function filterCustomerTypesByUserType(): CustomerType[] {
	if (useIsUnitUser()) {
		return Object.values(CustomerType)
	}
	if (isOrgUserForWallet()) {
		return [CustomerType.individual, CustomerType.business, CustomerType.individualWallet, CustomerType.businessWallet]
	}
	return [CustomerType.individual, CustomerType.business]
}

export default function Customers({
	token,
	resourcePath,
	limit = 25,
	includedColumns,
	enableTitle,
	fullHeight,
	refreshToken,
	onSelectCustomer,
}: CustomersProps) {
	const prefix = "cust"
	const userType = useUserType()
	const {addToast} = useToasts()
	const hookAccessToken = useAccessToken()
	const accessToken = token ?? hookAccessToken
	const statusFilterOptions = new Map([CustomerStatus.Active, CustomerStatus.Archived].map((v) => [v, v]))
	const typeFilterOptions = new Map(
		filterCustomerTypesByUserType().map((type) => [type, customerTypeToFilterOptions[type]])
	)
	const [sortBy, setSortBy] = useQueryState(`${prefix}-sort`, "-createdAt")
	const [query, setQuery] = useQueryState(`${prefix}-filter[query]`, "")
	const [filteredOrgs, setFilteredOrgs] = useQueryState<string[]>(`${prefix}-filter[orgs]`, [])
	const [filteredBanks, setFilteredBanks] = useQueryState<string[]>(`${prefix}-filter[banks]`, [])
	const [statuses, setStatuses] = useQueryState<CustomerStatus[]>(`${prefix}-filter[status]`, [CustomerStatus.Active])
	const [filteredTypes, setFilteredTypes] = useQueryState<CustomerType[]>(`${prefix}-filter[types]`, [])
	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) =>
			findCustomers(
				accessToken,
				offset,
				limit,
				sortBy,
				query,
				filteredOrgs,
				statuses,
				filteredTypes,
				filteredBanks,
				resourcePath
			),
		(x) => x.data.length,
		[
			sortBy,
			query,
			filteredOrgs.join(","),
			filteredBanks.join(","),
			statuses.join(","),
			refreshToken,
			filteredTypes.join(","),
			resourcePath,
		],
		prefix,
		true
	)
	const [searchBox, setIsSearchLoading] = SearchBox(query, "Search Customers", 500, onSearch)

	const isFiltering = filteredOrgs.length != 0 || filteredBanks.length != 0 || query != ""

	const agreements =
		userType === "org"
			? useAsyncResult(() => findClientOrgBankAgreement(accessToken, []), [])
			: AsyncResult.pending<ClientOrgBankAgreement[], ErrorDocument>()

	const banks =
		userType === "unit"
			? useAsyncResult(() => findBanks(accessToken, 0, 10000), [])
			: AsyncResult.pending<Bank[], ErrorDocument>()

	const orgs =
		userType === "unit" || userType === "bank"
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()

	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredOrgs, filteredOrgs, reset, limit),
		[orgs, setFilteredOrgs, filteredOrgs.join(","), reset, limit]
	)

	const bankFilter =
		userType === "unit"
			? banks.match(
					() => null,
					(banks) => (
						<Filter
							title="Banks"
							isSearchable
							setStatuses={setFilteredBanks}
							statuses={filteredBanks}
							onFilterFunc={() => reset(limit)}
							options={new Map<string, string>(banks.map((bank) => [bank.id, bank.attributes.name]))}
						/>
					),
					(_) => null
			  )
			: agreements.match(
					() => null,
					(agreements) => (
						<Filter
							title="Banks"
							isSearchable
							setStatuses={setFilteredBanks}
							statuses={filteredBanks}
							onFilterFunc={() => reset(limit)}
							options={
								new Map<string, string>(
									agreements.map((agreement: ClientOrgBankAgreement) => [
										agreement.relationships.bank.data.id,
										agreement.attributes.bankName || "",
									])
								)
							}
						/>
					),
					(_) => null
			  )

	const statusFilter = (
		<Filter
			statuses={statuses}
			setStatuses={setStatuses}
			onFilterFunc={() => reset(limit)}
			options={statusFilterOptions}
			title="Status"
		/>
	)

	const typeFilter = (
		<Filter
			statuses={filteredTypes}
			setStatuses={setFilteredTypes}
			onFilterFunc={() => reset(limit)}
			options={typeFilterOptions}
			title="Type"
		/>
	)

	useEffect(() => {
		result.match(
			() => null,
			() => setIsSearchLoading(false),
			() => setIsSearchLoading(false)
		)
	}, [result])

	function toggleSort() {
		setSortBy(sortBy == "-createdAt" ? "createdAt" : "-createdAt")
		reset(limit)
	}

	function onSearch(searchTerm: string) {
		setQuery(searchTerm)
		reset(limit)
	}

	async function exportCustomersFunc() {
		const response = await exportCustomers(
			accessToken,
			sortBy,
			query,
			filteredOrgs,
			filteredTypes,
			filteredBanks,
			resourcePath
		)

		response.match(
			(d) => downloadFile("Customers.csv", d, "text/csv"),
			() => {
				addToast("Something went wrong", {appearance: "warning"})
			}
		)

		return true
	}

	return (
		<DataTableCard className={"customers-card"}>
			<DataTableActionHeader
				enableSticky={fullHeight}
				searchBox={searchBox}
				exportFunc={exportCustomersFunc}
				filters={[typeFilter, orgFilter, bankFilter, statusFilter]}
				title={enableTitle ? "Customers" : null}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: customers}) => {
					return (
						<CustomersTable
							fullHeight={fullHeight}
							isFiltering={isFiltering}
							customers={customers.data}
							include={customers.included}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							isUsingPaging={true}
							includedColumns={includedColumns}
							meta={customers.meta}
							sortFunction={toggleSort}
							sortBy={sortBy}
							onSelect={onSelectCustomer}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
